import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {AppService} from "../../app.service";
import {ApiRequestService} from "../../shared/api-request.service";
import {UtilsService} from "../../shared/utils.service";
import {tap} from "rxjs/operators";
import {HazardModel} from "../../models/hazard.model";
import {HazardsEditComponent} from "../../hazards/hazards-edit/hazards-edit.component";
import {ApiDataSource} from "../../utils/api-data-source";
import {AssetModel} from "../../models/asset.model";
import {SitesHazardsViewComponent} from "../../sites/sites-hazards-view/sites-hazards-view.component";
import {HazardsSelectorComponent} from "../../hazards/hazards-selector/hazards-selector.component";
import {AssetsViewHazardsFilterComponent} from "../assets-view-hazards-filter/assets-view-hazards-filter.component";

@Component({
  selector: 'app-assets-view-hazards',
  templateUrl: './assets-view-hazards.component.html',
  styleUrls: ['./assets-view-hazards.component.scss']
})
export class AssetsViewHazardsComponent implements OnInit, AfterViewInit {

  @Input('asset_id') asset_id: number;
  @Input('asset') asset: AssetModel;

  dataSource: AssetsViewHazardsDataSource = new AssetsViewHazardsDataSource(this.app, this.api);

  listColumns: string[] = [
    'select',
    'id',
    'name',
    'status',
    'ira_score',
    'ira',
    'rra_score',
    'rra',
    'controls',
    'date_created',
    // 'date_created_UTC',
    'actions'
  ];

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService
  ) { }

  ngOnInit() {
    this.dataSource.asset_id = this.asset_id;
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange.pipe(tap((sorter) => {
      this.dataSource.order_by = sorter['active'];
      this.dataSource.order = sorter['direction'];

      // Sort UTC dates based on its corresponding date column.
      if ( sorter['active'] == 'date_created_UTC' ) {
        this.dataSource.order_by = 'date_created';
      }

      this.dataSource.getData();
    })).subscribe();
  }

  /**
   * Displays hazard data.
   *
   * @param {HazardModel} hazard - The hazard data to be displayed in the dialog.
   * @return {void}
   */
  onView(hazard: HazardModel): void {
    this.utils.showComponentDialog(
      SitesHazardsViewComponent,
      {
        id: hazard.id,
        hide_asset_link: true
      },
      { width: '900px' },
      (success) => {
        if (!success) { return; }
        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Create a new hazard.
   *
   * @returns {void}
   */
  onAdd(): void {
    const hazard = new HazardModel();
    hazard.asset_id = this.asset_id;
    this.onEdit(hazard);
  }

  /**
   * Create a new or update an existing hazard.
   *
   * @param hazard
   */
  onEdit(hazard: HazardModel): void {
    this.utils.showComponentDialog(
      HazardsEditComponent,
      { ...hazard },
      {},
      (success) => {
        // if (!success) { return; } // Removed to update list on close
        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Copy hazards to an asset.
   *
   * @return {void}
   */
  onCopy(): void {
    // show the hazard selector
    this.utils.showComponentDialog(
      HazardsSelectorComponent,
      {
        multiple: true
      }, {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.copyHazardsToAsset(results);
        }
      }
    );
  }

  /**
   * Copies selected hazards to the asset.
   *
   * @param {number[]} selected_hazard_ids An array of hazard IDs to be copied to the asset
   * @return {void}
   */
  copyHazardsToAsset(selected_hazard_ids: number[]): void {
    if (Array.isArray(selected_hazard_ids) && selected_hazard_ids.length > 0) {
      this.api.makeRequest('put', `v2/assets/${this.dataSource.asset_id}/hazards-copy`, {
        selected_hazard_ids: selected_hazard_ids
      })
        .then((response) => {
          this.dataSource.getData(true);
          this.utils.showToast('Hazard templates were copied.');
        })
        .catch((errorResponse) => {
          this.utils.handleAPIErrors(errorResponse);
        });
    }
  }

  /**
   * Removes a hazard by archiving it.
   *
   * @param hazard_id The ID of the hazard to be removed.
   * @return void
   */
  onRemove(hazard_id: number): void {
    this.utils.showModal(
      'Archive Hazard/Risk',
      'Are you sure you want to archive this Hazard/Risk? It will also be removed from ' + this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase() + ' if associated with any.',
      () => {
        this.remove(hazard_id, 'The Hazard/Risk was archived.');
      }
    );
  }

  /**
   * Removes selected hazards by archiving it.
   *
   * @return {void}
   */
  onRemoveSelected(): void {
    this.utils.showModal(
      'Archive Hazards/Risks',
      'Are you sure you want to archive the selected Hazards/Risks? They will also be removed from ' + this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase() + ' if associated with any.',
      () => {
        this.remove(this.dataSource.selection.selected.join(','), 'The selected Hazards/Risks were archived.');
      }
    );
  }

  /**
   * Removes hazards with the specified IDs.
   *
   * @param {any} ids - The hazard IDs to be removed.
   * @param msg
   * @return {void}
   */
  private remove(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v1/hazards/${ids}`, {}, {
      asset_id: this.asset.id
    })
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData(true);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Restore a hazard.
   *
   * @param {number} hazard_id - The ID of the Hazard/Risk to be restored.
   * @return {void}
   */
  onRestore(hazard_id: number): void {
    this.utils.showModal(
      'Restore Hazard/Risk',
      'Are you sure you want to restore this Hazard/Risk?',
      () => {
        this.restore(hazard_id, 'The Hazard/Risk was restored.');
      }
    );
  }

  /**
   * Restore selected hazards.
   *
   * @return {void}
   */
  onRestoreSelected(): void {
    this.utils.showModal(
      'Restore Hazards/Risks',
      'Are you sure you want to restore the selected Hazards/Risks?',
      () => {
        this.restore(this.dataSource.selection.selected.join(','), 'The selected Hazards/Risks were restored.');
      }
    );
  }

  /**
   * Removes hazards with the specified IDs.
   *
   * @param {any} ids - The hazard IDs to be removed.
   * @param msg
   * @return {void}
   */
  private restore(ids: any, msg: string): void {
    this.api.makeRequest('put', `v1/hazards/${ids}/restore`, {}, {
      asset_id: this.asset.id
    })
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData(true);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Show component dialog for filtering hazards.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(
      AssetsViewHazardsFilterComponent,
      {
        status: this.dataSource.status,
        archived: this.dataSource.archived
      },
      {
        width: '500px'
      },
      (results: any) => {
        if (typeof results !== 'undefined') {
          this.dataSource.status = results.status ?? this.dataSource.status;
          this.dataSource.archived = results.archived ?? this.dataSource.archived;
          this.dataSource.getData();
        }
      }
    );
  }

  /**
   * Calculates and returns the count of hazard controls.
   *
   * @param {any} controls - The array or object containing hazard controls.
   * @return {string} The number of hazard controls. Returns '0' if the input does not contain any controls.
   */
  getControlsCount(controls: any): string {
    if (!controls) {
      return '0';
    }

    try {
      // Parse the controls.
      const parsedControls = JSON.parse(controls);
      // Check if we have a value for length.
      return parsedControls?.length ? parsedControls.length.toString() : '0';
    } catch {
      return '0';
    }
  }
}

export class AssetsViewHazardsDataSource extends ApiDataSource {
  order_by = 'date_created';
  order = 'desc';
  asset_id: number;
  status: string = '';
  archived = 'false';

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/assets/${this.asset_id}/hazards`, resetOffset, {
      status: this.status,
      archived: this.archived
    })
  }
}
